import currency from 'currency.js'
import { AMOUNT_STRATEGIES } from 'src/models/booking-fee-type/BookingFeeType'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'

/**
 * Computes the price of a Booking Fee OrderItem.
 */
class BookingFeePriceComputer {
  static #instance = null

  constructor () {
    if (!BookingFeePriceComputer.#instance) {
      BookingFeePriceComputer.#instance = this
    }
    return BookingFeePriceComputer.#instance
  }

  /**
   * Compute the highest priority BookingFeeType for the given Item OrderItem,
   * based on the OrderItem's state and any conditions configured for each BookingFeeType.
   * @param {OrderItem} orderItem
   * @returns {Number} price
   */
  computeForBookingFeeOrderItem (orderItem) {
    let price = 0

    if (orderItem.type !== ORDER_ITEM_TYPES.BOOKING_FEE) {
      return price
    }

    if (_.isNil(orderItem.bookingFeeType)) {
      return price
    }

    if (orderItem.bookingFeeType.amountStrategy === AMOUNT_STRATEGIES.FIXED) {
      price = orderItem.bookingFeeType.amount
    } else if (orderItem.bookingFeeType.amountStrategy === AMOUNT_STRATEGIES.PERCENTAGE) {
      const parentPrice = this.#bookingFeeParentPrice(orderItem)
      price = currency(parentPrice).multiply(orderItem.bookingFeeType.percentThousandths).divide(100000).value
    }

    return price
  }

  computeForBookingFeeType (bookingFeeType, orderItem) {
    let price = 0

    if (_.isNil(orderItem)) {
      return price
    }

    if (bookingFeeType.amountStrategy === AMOUNT_STRATEGIES.FIXED) {
      price = bookingFeeType.amount
    } else if (bookingFeeType.amountStrategy === AMOUNT_STRATEGIES.PERCENTAGE) {
      if (orderItem.setAsGross) {
        price = currency(orderItem.pricing.total).multiply(bookingFeeType.percentThousandths).divide(100000).value
      } else {
        price = currency(orderItem.pricing.subtotal).multiply(bookingFeeType.percentThousandths).divide(100000).value
      }
    }

    return price
  }

  // private

  /**
   * Gets the appropriate channel for the given OrderItem.
   * The channel is determined by the being application used to place the given OrderItem.
   * @param {OrderItem} orderItem
   * @returns {OrderItem} parent
   */
  #bookingFeeOrderItemParent (orderItem) {
    const orderItemParent = orderItem.parent
    let parent = null

    if (!orderItemParent.isCollection && orderItemParent.type === 'OrderItem') {
      parent = orderItemParent
    } else {
      parent = _.find(orderItem.order?.orderItems?.data, orderItem => orderItem.orderItemBookingFee === orderItem)
    }

    return parent
  }

  /**
   * Gets the price of the parent OrderItem for the given Booking Fee OrderItem.
   * @param {OrderItem} orderItem
   * @returns {Number} price
   */
  #bookingFeeParentPrice (orderItem) {
    const parent = this.#bookingFeeOrderItemParent(orderItem)
    if (_.isNil(parent)) {
      return 0
    }

    if (parent.setAsGross) {
      return currency(parent.pricing.total).value
    } else {
      return currency(parent.pricing.subtotal).value
    }
  }
}

const bookingFeePriceComputer = new BookingFeePriceComputer()
export default bookingFeePriceComputer
